import type {
  InstallationResolveAddress,
  InstallationResolveLineItem,
  InstallationResolvePayload,
  InstallationResolveSpecialFee,
} from "../types/installation";

export type BuildInstallationResolveInput = {
  orderNumber?: string;
  cartId: string;
  dynamicCostId: string;
  signageId: string;
  sourceId: string;
  customerId: string;
  inventorySource: string;
  installationCost: number;
  installationTaxable: boolean;
  additionalCost?: number;
  discountCode?: string;
  paymentMethod?: string;
  userType?: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  brokerageFirmId?: string | null;
  brokerageFirmName?: string | null;
  brokerageFirmAdmin?: string;
  teamMemberId?: string | null;
  teamMemberName?: string;
  office?: unknown;
  agentId?: string | null;
  agentName?: string | null;
  billingAddress?: InstallationResolveAddress | null;
  installationAddress?: InstallationResolveAddress | null;
  lineItems: InstallationResolveLineItem[];
  specialFees: InstallationResolveSpecialFee[];
};

function toResolveAddress(raw: unknown): InstallationResolveAddress | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const details = (a.address_details ?? a) as Record<string, unknown>;
  return {
    label: String(a.label ?? ""),
    first_name: String(a.first_name ?? ""),
    last_name: String(a.last_name ?? ""),
    company: String(a.company ?? ""),
    default_address: Boolean(a.default_address),
    address_details: {
      addressLine1: String(details.addressLine1 ?? ""),
      addressLine2: String(details.addressLine2 ?? ""),
      city: String(details.city ?? ""),
      state: String(details.state ?? details.stateCode ?? ""),
      country: String(details.country ?? details.countryCode ?? "US"),
      zip: String(details.zip ?? ""),
      stateCode: String(details.stateCode ?? details.state ?? ""),
      countryCode: String(details.countryCode ?? details.country ?? "US"),
      ...(Number.isFinite(Number(details.latitude))
        ? { latitude: Number(details.latitude) }
        : {}),
      ...(Number.isFinite(Number(details.longitude))
        ? { longitude: Number(details.longitude) }
        : {}),
    },
  };
}

/**
 * Build installation resolve payload — all fields inside `payload` per backend contract.
 * No client-side tax/discount math.
 */
export function buildInstallationResolvePayload(
  input: BuildInstallationResolveInput
): InstallationResolvePayload {
  const billing = toResolveAddress(input.billingAddress);
  const install = toResolveAddress(input.installationAddress);

  return {
    ...(input.orderNumber ? { order_number: String(input.orderNumber).trim() } : {}),
    cart_id: String(input.cartId || "").trim() || undefined,
    dynamic_cost_id: String(input.dynamicCostId || "").trim() || undefined,
    signage_id: String(input.signageId || "").trim(),
    source_id: String(input.sourceId || "").trim() || undefined,
    brokerage_firm_id: input.brokerageFirmId ?? null,
    brokerage_firm_name: input.brokerageFirmName ?? null,
    brokerage_firm_admin: input.brokerageFirmAdmin ?? "",
    team_member_id: input.teamMemberId ?? null,
    team_member_name: input.teamMemberName ?? "",
    customer_id: String(input.customerId || "").trim() || undefined,
    office: input.office ?? undefined,
    agent_id: input.agentId ?? null,
    agent_name: input.agentName ?? null,
    customer_first_name: input.customerFirstName ?? "",
    customer_last_name: input.customerLastName ?? "",
    customer_email: input.customerEmail ?? "",
    customer_phone: input.customerPhone ?? "",
    inventory_source: input.inventorySource || "agent",
    user_type: input.userType ?? "",
    user_name: input.userName ?? "",
    user_email: input.userEmail ?? "",
    user_phone: input.userPhone ?? "",
    payment_method: input.paymentMethod ?? "card",
    discount_code: String(input.discountCode ?? "").trim(),
    installation_cost: Number(input.installationCost) || 0,
    additional_cost: Number(input.additionalCost) || 0,
    is_taxable: input.installationTaxable,
    itemType: "InstallationCost",
    billing_address: billing,
    installation_details: {
      installation_address: install,
    },
    line_items: input.lineItems,
    specialfee: input.specialFees,
  };
}
